import {interval, Subscription} from "rxjs";
import {isPlatformBrowser} from "@angular/common";
import {Component, inject, NgZone, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';

import {NgbModalRef} from "@ng-bootstrap/ng-bootstrap";

import {AdStatus} from "@numizmat/enumerations";
import {AlertService, ModalService} from "@numizmat/services/common";
import {PublicAdDetailResponse} from "@numizmat/services-models/core";
import {AdDetailStateService} from "@numizmat/services/core/ad-detail/ad-detail-state.service";
import {AdLikesModalComponent} from "@numizmat/pages/ad-detail-page/modals/ad-likes-modal/ad-likes-modal.component";
import {AdVisitsModalComponent} from "@numizmat/pages/ad-detail-page/modals/ad-visits-modal/ad-visits-modal.component";

@Component({
  selector: 'nz-ad-time-panel',
  standalone: true,
  imports: [],
  templateUrl: './ad-time-panel.component.html',
  styleUrl: './ad-time-panel.component.scss'
})
export class AdTimePanelComponent implements OnInit, OnDestroy {
  private ngZone: NgZone = inject(NgZone);
  private platformId: Object = inject(PLATFORM_ID);
  private modalService: ModalService = inject(ModalService);
  private alertService: AlertService = inject(AlertService);
  private adDetailStateService: AdDetailStateService = inject(AdDetailStateService);

  private responseRemainingDays = 0;
  private responseRemainingHours = 0;
  private responseRemainingMinutes = 0;
  private responseRemainingSeconds = 0;
  private subs: Subscription = new Subscription();
  private intervalSubscription?: Subscription;

  protected remainingDays = 0;
  protected remainingHours = 0;
  protected remainingMinutes = 0;
  protected remainingSeconds = 0;
  protected adDetails!: PublicAdDetailResponse;

  public ngOnInit(): void {
    this.getAdDetails();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.intervalSubscription?.unsubscribe();
  }

  protected showBidExtensionTimeInfo(): void {
    this.alertService.showBidExtensionTimeInfo();
  }

  protected onOpenAdLikesModal(): void {
    const modalRef: NgbModalRef = this.modalService.open(AdLikesModalComponent, {
      scrollable: true
    });
    modalRef.componentInstance.adId = this.adDetails.adId;
    modalRef.componentInstance.adTitle = this.adDetails.title;
  }

  protected onOpenAdVisitsModal(): void {
    const modalRef: NgbModalRef = this.modalService.open(AdVisitsModalComponent, {
      scrollable: true
    });
    modalRef.componentInstance.adId = this.adDetails.adId;
    modalRef.componentInstance.adTitle = this.adDetails.title;
  }

  private getAdDetails() {
    const subscription: Subscription = this.adDetailStateService.getPublicAdDetails().subscribe({
      next: (adDetails: PublicAdDetailResponse | undefined) => {
        if (!!adDetails) {
          this.adDetails = adDetails;
          if (this.adDetails.adStatus === AdStatus.Active) {
            if (this.responseRemainingDays !== this.adDetails.remainingDays ||
              this.responseRemainingHours !== this.adDetails.remainingHours ||
              this.responseRemainingMinutes !== this.adDetails.remainingMinutes ||
              this.responseRemainingSeconds !== this.adDetails.remainingSeconds) {

              this.responseRemainingDays = this.adDetails.remainingDays;
              this.responseRemainingHours = this.adDetails.remainingHours;
              this.responseRemainingMinutes = this.adDetails.remainingMinutes;
              this.responseRemainingSeconds = this.adDetails.remainingSeconds;

              this.remainingDays = this.responseRemainingDays;
              this.remainingHours = this.responseRemainingHours;
              this.remainingMinutes = this.responseRemainingMinutes;
              this.remainingSeconds = this.responseRemainingSeconds;
              this.setInterval();
            }
          } else {
            this.resetTimer();
          }
        }
      }
    });
    this.subs.add(subscription);
  }

  private setInterval(): void {
    if (isPlatformBrowser(this.platformId) && !this.intervalSubscription) {
      this.ngZone.runOutsideAngular(() => {
        this.intervalSubscription = interval(1000).subscribe(() => {
          this.ngZone.run(() => {
            if (this.remainingSeconds > 0) {
              this.remainingSeconds--;
            } else {
              if (this.remainingMinutes > 0) {
                this.remainingMinutes--;
                this.remainingSeconds = 59;
              } else {
                if (this.remainingHours > 0) {
                  this.remainingHours--;
                  this.remainingMinutes = 59;
                  this.remainingSeconds = 59;
                } else {
                  if (this.remainingDays > 0) {
                    this.remainingDays--;
                    this.remainingHours = 23;
                    this.remainingMinutes = 59;
                    this.remainingSeconds = 59;
                  } else {
                    this.intervalSubscription?.unsubscribe();
                  }
                }
              }
            }
          });
        });
      });
    }
  }

  private resetTimer(): void {
    this.remainingDays = 0;
    this.remainingHours = 0;
    this.remainingMinutes = 0;
    this.remainingSeconds = 0;
    this.intervalSubscription?.unsubscribe();
    this.intervalSubscription = undefined;
  }
}
